import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';

import * as AuthActions from './auth.actions';
import { CognitoAuthService } from '@app/core/services/cognito-auth.service';
import { Router } from '@angular/router';
import { landingRouteFor } from '@app/core/routing/landing-route';
import { CurrentUserDetails, CurrentUserDetailsService } from '@app/core/services/current-user-details.service';
import { Action } from '@ngrx/store';
import { UserRoles } from '@app/core/enums/userRoles.enum';
import { User } from '@app/core/interfaces';

@Injectable()
export class AuthEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private cognitoAuthService: CognitoAuthService,
    private router: Router,
    private currentUserDetailsService: CurrentUserDetailsService,
  ) { }

  ngrxOnInitEffects(): Action {
    // Restoring an Amplify session is asynchronous, so this only asks; restoreSession$
    // below answers with UpdateUserAuthDetails once Amplify has resolved the tokens.
    return AuthActions.SessionRestoreRequested();
  }

  /**
   * Rehydrates the store from the session Amplify persisted, so a refresh or a new tab
   * does not drop a signed-in user back to the login form.
   */
  restoreSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.SessionRestoreRequested),
      switchMap(() =>
        from(this.cognitoAuthService.currentUser$()).pipe(
          switchMap((user) => user
            ? this.currentUserDetailsService.getAuthDetails$().pipe(
                map((details) => AuthActions.UpdateUserAuthDetails({
                  isAuthenticated: true,
                  user: this.mergeUserWithDetails(user, details)
                }))
              )
            : of(AuthActions.UpdateUserAuthDetails({ isAuthenticated: false, user: null }))
          ),
          catchError(() =>
            of(AuthActions.UpdateUserAuthDetails({ isAuthenticated: false, user: null }))
          )
        )
      )
    )
  );

  /**
   * Google returns the browser to the login route, and restoreSession$ only fills the store.
   * A signed-in user is sent on from there to their landing page, so the Google round trip
   * (or a stale tab) doesn't strand them on the sign-in page.
   */
  leaveLoginWhenAuthenticated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.UpdateUserAuthDetails),
        tap(({ isAuthenticated, user }) => {
          if (isAuthenticated && user && this.router.url.startsWith('/account/login')) {
            this.router.navigate([landingRouteFor(user.roles ?? [user.role ?? ''], user.employmentStatus)]);
          }
        })
      ),
    { dispatch: false });

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LogoutRequested),
        switchMap(() =>
          from(this.cognitoAuthService.signOut$()).pipe(
            map(() => AuthActions.LogoutSuccess()),
            // The local session is cleared by Amplify even when the hosted sign-out call
            // fails, so the user is still logged out here — reporting failure would leave
            // the store claiming they are signed in when they are not.
            catchError(() => of(AuthActions.LogoutSuccess()))
          )
        )
      )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LogoutSuccess),
        tap(() => this.router.navigate(['/account/login']))
      ),
    {
      dispatch: false
    }
  );

  private mergeUserWithDetails(user: User, details: CurrentUserDetails): User {
    const tokenHasExplicitRole = !(
      (user.roles?.length === 1 && user.roles[0] === UserRoles.user) ||
      (!user.roles?.length && user.role === UserRoles.user)
    );
    const detailsHaveRoles = Boolean(details.user.role || details.user.roles?.length);
    return {
      ...details.user,
      ...user,
      // Older accounts without a Cognito group use the User fallback in the id token.
      // Preserve their database-backed Employee role while keeping explicit Cognito
      // groups (particularly Admin) authoritative.
      ...(tokenHasExplicitRole || !detailsHaveRoles ? {} : { role: details.user.role, roles: details.user.roles })
    };
  }
}
